import { formatDate } from '@angular/common';
import { Component, Inject, Injector, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import * as moment from 'moment';

// import & install Swiper core and required modules
import { MatDialog } from '@angular/material/dialog';
import { ChartOptions } from 'chart.js';
import { ThemeService } from 'ng2-charts';
import SwiperCore, { Mousewheel, Navigation, Pagination, Scrollbar, SwiperOptions } from 'swiper';
import { AppSettings } from '../../../app-settings.service';
import { AppConstants } from '../../../app.constants';
import { MobileViewService } from '../../../utils/shared-services/mobileView/mobileView.service';
import { WidgetConstants } from '../constants/widget.constants';
import { HODateSelectionDialog } from './dialogs/date-selection-dialog.component';
import { BrowserStorage } from '../../../utils/shared-services/browser-storage.service';
import { SelectedDateData } from '../models/head-office-portal.model';
import { HOPortalService } from '../services/head-office-portal.service';
import { StoreApiService } from '../../stores/services/store.resource';
import { SelectHOStoreDialog } from './dialogs/select-ho-store-dialog.component';
import { StoreBase } from '../../stores/models/store.model';
import { HOPortalApiService } from '../services/head-office-portal.resource';
import { ConceptApiService } from '../../concepts/services/concept.resource';
import { HoReportViewerDialog } from './dialogs/ho-report-viewer-dialog.component';
import { GroupWithItems } from '../../store-groups/models/group-item.model';
import { StoreGroupApiService } from '../../store-groups/services/store-group.resource';
import { Tag } from '../../tags/models/tag.model';
import { TagApiService } from '../../tags/services/tag.resource';

SwiperCore.use([Mousewheel, Navigation, Pagination, Scrollbar]);

@Component({
    selector: 'headOfficePortal',
    templateUrl: `src/app/features/head-office-portal/templates/head-office-portal.html`,
    standalone: false
})
export class HOPortalComponent implements OnInit {
    public captions: Record<string, string>;
    public THEMES = AppConstants.THEMES;
    public themeOptions: ChartOptions;
    public myStoresToggled: boolean;
    public EmpNum: string;
    public storeInfo: { status?: number } = {};
    public widgetsInfo: any = {};
    public hideProd: boolean;
    public datePickerValue: string;
    public passedDate: SelectedDateData;
    public data = {};
    public viewsConfig: any = [];
    public selectedViewIdx: number = 0;
    public priorSevenDays: string[] = [];
    public searchStr: string = '';
    public stores: any;
    public storeList: string[] = [];
    public storesSelected: number = 0;
    public totalStores: number = 0;
    public conceptID: number;
    public conceptIDFromParams: number;
    public storeGroups: GroupWithItems[];
    public tags: Tag[];
    public swiperConfig: SwiperOptions = {};
    public employeeName: string = '';

    primaryStore: number;
    storeNumList: any;
    hoPortalData: any;

    constructor(
        @Inject(ActivatedRoute) private actRoute: ActivatedRoute,
        @Inject(AppSettings) private appSettings: AppSettings,
        @Inject(ThemeService) private themeService: ThemeService,
        @Inject(MobileViewService) private mobileViewService: MobileViewService,
        @Inject(BrowserStorage) private browserStorage,
        @Inject(MatDialog) private dialog: MatDialog,
        @Inject(HOPortalService) private hoPortalService: HOPortalService,
        @Inject(HOPortalApiService) private hoPortalApiService: HOPortalApiService,
        @Inject(StoreApiService) private storeApiService: StoreApiService,
        @Inject(Injector) private injector: Injector,
        @Inject(ConceptApiService) private conceptApiService: ConceptApiService,
        @Inject(StoreGroupApiService) private storeGroupApiService: StoreGroupApiService,
        @Inject(TagApiService) private tagApiService: TagApiService,
    ) {
        this.mobileViewService.setBreakpoint(MobileViewService.MOBILE_BREAKPOINT_MEDIUM);
        this.myStoresToggled = true;
        this.widgetsInfo = this.hoPortalService.getWidgetsInfo();
        this.mobileViewService.setBreakpoint(MobileViewService.MOBILE_BREAKPOINT_MEDIUM);
        this.swiperConfig = {
            breakpoints: {
                900: { direction: 'horizontal', slidesPerView: 2 },
                1400: { direction: 'horizontal', slidesPerView: 3 },
            },
            centerInsufficientSlides: true,
            initialSlide: 0,
            direction: 'vertical',
            mousewheel: true,
            pagination: { clickable: true },
            slidesPerView: 1,
            spaceBetween: 20,
            noSwiping: true, // add swiper-no-swiping class to elements
        };
    }

    ngOnInit(): void {
        this.actRoute.data.subscribe((data) => {
            this.captions = data.captions;
        });
        this.actRoute.params.subscribe(params => {
            this.EmpNum = params['empNum'];
        });

        this.hoPortalData = this.hoPortalService.getHoPortalData();
        this.conceptID = this.hoPortalData.ConceptID ? this.hoPortalData.ConceptID : this.browserStorage.getSessionstorage('portalConceptID', null);
        this.primaryStore = this.hoPortalData.StoreID;

        this.loadConceptConfig();

        // hide from PROD only -- currently nothing is hidden
        if (
            window.location.origin.indexOf('localhost') > -1 ||
            window.location.origin.indexOf('ci') > -1 ||
            window.location.origin.indexOf('qa') > -1
        ) {
            this.hideProd = true;
        }
        this.storeInfo.status = 1;

        let data = this.hoPortalData.DataSets.DataSet;
        if (!data) {
            return;
        }
        this.employeeName = data[3].Record ? data[3].Record.POSNAME : '';
        const storeList = data[4].Record;
       
        // Get the most recent MAXOPENDATE
        const mostRecentDate = storeList
            .map(storeData => new Date(storeData.MAXOPENDATE))
            .reduce((latest, current) => current > latest ? current : latest);
        const stores = storeList.map(record => record.STOREID.toString());
        this.storeNumList = storeList.map(record => record.STORENUM.toString());
        this.storeList = stores;
        this.totalStores = stores.length;
        this.storesSelected = stores.length;
        this.storeGroups = history.state.storeGroups || [];
        this.tags = history.state.tags || [];

        this.storeApiService.getStoreListByStoreIDs(stores).then((stores: StoreBase[]) => {
            this.stores = stores.map(store => ({
                ...store,
                isChecked: true, // default to all stores being selected
                hoStore: storeList.filter((s) => s.STOREID === store.id)[0]
            }));
        });
        const storePromises: Promise<any>[] = [];
        if (this.storeGroups.length <= 0) {
            storePromises.push(this.storeGroupApiService.getAllStoreGroupsWithItemsForConcept(this.conceptID)
                .then(groups => this.storeGroups = groups));
        }
        if (this.tags.length <= 0) {
            storePromises.push(this.tagApiService.getAllTagsForConcept(this.conceptID).then(tags => this.tags = tags));
        }

        let startTime = mostRecentDate.getTime();
        for (var i = 0; i < 7; i++) {
            this.priorSevenDays[i] = formatDate(
                startTime - i * 24 * 60 * 60 * 1000,
                WidgetConstants.DATE_FORMAT_SIMPLE,
                'en-US'
            );
        }
        this.datePickerValue = this.priorSevenDays[0];
        this.passedDate = {
            date1: moment(this.datePickerValue, "YYYY-MM-DD").toDate(),
            date2: moment(this.datePickerValue, "YYYY-MM-DD").toDate(),
            selectedDate: ""
        };
    }

    toggleTheme() {
        this.appSettings.setTheme(
            this.appSettings.getTheme() === this.THEMES.dark ? this.THEMES.light : this.THEMES.dark
        );
        this.appSettings.applyTheme();
        this.updateChartColorScheme();
    }

    updateChartColorScheme() {
        this.themeOptions = {
            scales: {
                x: {
                    suggestedMin: 0,
                    grid: {
                        color: getComputedStyle(document.body).getPropertyValue('--tertiary'),
                    },
                    ticks: {
                        color: getComputedStyle(document.body).getPropertyValue('--text-color-primary'),
                    },
                },
                y: {
                    suggestedMin: 0,
                    grid: {
                        color: getComputedStyle(document.body).getPropertyValue('--tertiary'),
                    },
                    ticks: {
                        color: getComputedStyle(document.body).getPropertyValue('--text-color-primary'),
                    },
                },
            },
        };
        this.themeService.setColorschemesOptions(this.themeOptions);
    }

    loadConceptConfig() {
        this.conceptApiService.getPortalConfigByConceptID(this.conceptID).then((config: any) => {
            this.viewsConfig = this.hoPortalService.parseSavedConfig(config);
            if (this.selectedViewIdx >= this.viewsConfig.length) {
                this.selectedViewIdx = this.viewsConfig.length - 1;
            }
            this.getData();
        });
    }

    openDateDialog(): void {
        const dialogRef = this.dialog.open(HODateSelectionDialog, {
            panelClass: 'app-full-bleed-dialog',
            maxWidth: '90vw',
            data: {
                selectedDate: this.datePickerValue,
            },
        });

        dialogRef.afterClosed().subscribe((result: string) => {
            if (result) {
                this.changeDate(formatDate(result, WidgetConstants.DATE_FORMAT_SIMPLE, 'en-US'));
            }
        });
    }

    selectHOStore(): void {
        const dialogRef = this.dialog.open(SelectHOStoreDialog, {
            panelClass: 'app-full-bleed-dialog',
            maxWidth: '950vw',
            minWidth: '50vw',
            data: {
                storeList: this.stores,
                storeGroups: this.storeGroups,
                tags: this.tags,
                totalStores: this.totalStores,
            },
        });

        dialogRef.afterClosed().subscribe((res: { storeID: number, storeName: string }[]) => {
            if (res && res.length > 0) {
                this.storesSelected = res.length;
                this.getData();
            } 
        });
    }

    getData() {
        let widgetsInView = this.viewsConfig[this.selectedViewIdx].widgets;
        let uniqueDatapoints = new Set();
        uniqueDatapoints.add('CONCEPT_Info_Current');
        widgetsInView.forEach((widget) => {
            this.widgetsInfo[widget.id].datapoints.forEach((datapoint) => {
                if (!this.data[datapoint]) {
                    uniqueDatapoints.add(datapoint);
                }
            });
            this.browserStorage.setSessionstorage(widget.id, this.widgetsInfo[widget.id].datapoints);
        });
        let datapoints = Array.from(uniqueDatapoints);
 
        let data = this.hoPortalData.DataSets.DataSet[4];
        this.storeNumList = this.stores ? this.stores.filter(store => store.isChecked).map(store => store.hoStore.STORENUM) : data.Record.map(record => record.STORENUM);
        const params = JSON.stringify({ 
            opendate: this.datePickerValue ? formatDate(this.datePickerValue, WidgetConstants.DATE_FORMAL_COMPACT, 'en-US') : formatDate("1899-01-01", WidgetConstants.DATE_FORMAL_COMPACT, 'en-US'), 
            hoStoreList: this.storeNumList.toString() 
        });
        this.hoPortalApiService.getDatasetsByDatapoints(this.conceptID, params, datapoints.toString()).then(
            (res: any) => {
                if (!res?.DataSet) {
                    return;
                }
                let datasets = Array.isArray(res.DataSet) ? res.DataSet : [res.DataSet];
                datasets.forEach((dataset) => (this.data[dataset.CodeName] = dataset));
                this.setWidgetsData(widgetsInView);
            },
            (error) => {
                widgetsInView.forEach((widget) => {
                    widget.title = this.widgetsInfo[widget.id].title;
                    widget.data = [];
                    widget.isTimedOut = true;
                    widget.injector = Injector.create({
                        providers: [{ provide: Object, useValue: widget }],
                        parent: this.injector,
                    });
                });
            }
        );
    }

    clearSavedData() {
        this.data = {};
        this.viewsConfig[this.selectedViewIdx].widgets.forEach((widget) => {
            widget.title = '';
            widget.isLoaded = false;
        });
    }

    changeDate(date: string): void {
        this.datePickerValue = date;
        this.passedDate = {
            date1: moment(date, "YYYY-MM-DD").toDate(),
            date2: moment(date, "YYYY-MM-DD").toDate(),
            selectedDate: ""
        };
        this.browserStorage.setSessionstorage('widgetSelectedDate', date);
        this.clearSavedData();
        this.getData();
    }

    setWidgetsData(widgets) {
        widgets.forEach((widget) => {
            widget.title = this.widgetsInfo[widget.id].title;
            widget.data = [];
            if (widget.id === 'wid-001') {
                widget.data.push(this.storeNumList);
            } else {
                this.widgetsInfo[widget.id].datapoints.forEach((datapoint) => {
                    widget.data.push(this.data[datapoint])
                });
            }

            widget.injector = Injector.create({
                providers: [{ provide: Object, useValue: widget }],
                parent: this.injector,
            });
            widget.isLoaded = true;
            widget.isError = false;
        });
    }

    checkWidgetType(widget: any): boolean {
        // switch (widget.id) {
        //     case 'wid-012':
        //         return this.isWidgetVisible(widget);
        //     case 'wid-013':
        //         return this.isWidgetVisible(widget);
        //     case 'wid-014':
        //         return this.isWidgetVisible(widget);
        //     case 'wid-015':
        //         return this.isWidgetVisible(widget);
        //     case 'wid-016':
        //         return this.isWidgetVisible(widget);
        //     case 'wid-017':
        //         return this.isWidgetVisible(widget);
        //     default:
        //         return true;
        // }
        return false;
    }

    // the widgets are only visible when the store is open date matches the selected date
    // isWidgetVisible(widget): boolean {
    //     const widgetsToShow = ['wid-012', 'wid-013', 'wid-014', 'wid-015', 'wid-016', 'wid-017'];
    //     return this.datePickerValue === this.storeInfo.openDate && widgetsToShow.includes(widget.id);
    // }

    openReportViewerDialog(): void {
        const dialogRef = this.dialog.open(HoReportViewerDialog, {
            disableClose: true,
            panelClass: 'app-full-bleed-dialog',
            maxWidth: '200vw',
            data: {
                ConceptID: this.conceptID,
                StoreList: this.storeList,
                StoreNumList: this.storeNumList,
                StoreID: this.primaryStore,
                EmpNum: this.EmpNum,
                PassedStartDate: this.passedDate.date1,
                PassedEndDate: this.passedDate.date2
            },
        });

        dialogRef.afterClosed().subscribe((result: SelectedDateData) => {
            if (result) {
                this.passedDate = { date1: result.date1, date2: result.date2, selectedDate: "" }
            }
        });
    }
    
    optionClicked(idx) {
        this.selectedViewIdx = idx;
        this.getData();
    }

    slideChange(ev) {
        this.swiperConfig.initialSlide = ev[0].activeIndex;
    }
}
